import React, {type JSX} from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import {CloseIcon} from '../../assets/icons/closeIcon';
import {type UploadedFile} from '../../hooks/useUploadRecords';
import {formatBytes} from '../../utils/formatBytes';
import {CustomizedCard} from './customizedCard';

type Props = {
  file: UploadedFile['file'];
  onSelect: () => void;
  onDelete: () => void;
  onRetry: () => void;
  isSelected: boolean;
  hasError: boolean;
  isUploaded: boolean;
  isLoading: boolean;
  testId: string;
};

const StyledFileItemHeaderWrapper = styled('div')(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(1),
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  wordBreak: 'break-all',
}));

const StyledFileSizeWrapper = styled('span')(({theme}) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
}));

const StyledFooterFileWrapper = styled('div')({
  display: 'flex',
  gap: '10px',
  alignItems: 'center',
  flexWrap: 'wrap',
});

const StyledLoadingIndicatorWrapper = styled('div')(({theme}) => ({
  color: theme.palette.info.main,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
}));

const StyledDivider = styled('span')(({theme}) => ({
  color: theme.palette.divider,
}));

const StyledRetryLink = styled(Link)(({theme}) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

export function FileItem({
  file,
  isSelected,
  onSelect,
  onDelete,
  onRetry,
  hasError,
  isUploaded,
  isLoading,
  testId,
}: Readonly<Props>): JSX.Element {
  return (
    <CustomizedCard
      handleClick={onSelect}
      isSelected={isSelected}
      testId={testId}
    >
      <StyledFileItemHeaderWrapper>
        <Typography variant="subtitle1">{file.name}</Typography>

        <Tooltip title="Remove file">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
            data-testid={`remove-file-${file.name}`}
          >
            <CloseIcon fontSize="inherit" viewBox="-3 -3 20 20" />
          </IconButton>
        </Tooltip>
      </StyledFileItemHeaderWrapper>
      <StyledFooterFileWrapper>
        <StyledFileSizeWrapper>{formatBytes(file.size)}</StyledFileSizeWrapper>

        {isLoading ? (
          <>
            <StyledDivider>&bull;</StyledDivider>
            <StyledLoadingIndicatorWrapper data-testid={`loading-${file.name}`}>
              Loading <CircularProgress size={18} />
            </StyledLoadingIndicatorWrapper>
          </>
        ) : null}

        {hasError ? (
          <>
            <StyledDivider>&bull;</StyledDivider>
            <Box component="span" fontWeight={600} color="error.main">
              Loading failed
            </Box>
            <StyledDivider>&bull;</StyledDivider>
            <StyledRetryLink
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onRetry();
              }}
              href="/#"
              underline="hover"
              data-testid={`retry-${file.name}`}
            >
              Retry
            </StyledRetryLink>
          </>
        ) : null}

        {isUploaded ? (
          <>
            <StyledDivider>&bull;</StyledDivider>
            <Box component="span" fontWeight={600} color="success.main">
              Uploaded
            </Box>
          </>
        ) : null}
      </StyledFooterFileWrapper>
    </CustomizedCard>
  );
}
